import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Image,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { PAYMENT_ACCOUNT } from "../../constants/payment";
import { createManualPaymentOrder } from "../../api/manualPaymentOrders";
import { formatNaira, parseMoney } from "../../utils/money";
import {
  buildManualPaymentWhatsAppMessage,
  buildWhatsAppUrl,
} from "../../utils/whatsappPayload";

const preparingMessages = [
  "Preparing payment details",
  "Generating payment reference",
  "Finalizing payment instructions",
];

const isHtmlResponse = (value) => {
  const text = String(value || "").trim().toLowerCase();

  return (
    text.startsWith("<!doctype html") ||
    text.startsWith("<html") ||
    text.includes("<body") ||
    text.includes("<title>403 forbidden</title>") ||
    text.includes("csrf verification failed") ||
    text.includes("origin checking failed")
  );
};

const extractApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return "Unable to submit payment proof. Please check your connection and try again.";
  }

  if (typeof data === "string") {
    if (isHtmlResponse(data)) {
      return "The server blocked this request. Please refresh the page and try again.";
    }

    return data || "Unable to submit payment proof. Please try again.";
  }

  const possibleMessage =
    data?.proof_of_payment?.[0] ||
    data?.unit_price?.[0] ||
    data?.subtotal?.[0] ||
    data?.total_amount?.[0] ||
    data?.shipping_fee?.[0] ||
    data?.customer_name?.[0] ||
    data?.customer_phone?.[0] ||
    data?.detail ||
    data?.message ||
    data?.error;

  if (possibleMessage) {
    return possibleMessage;
  }

  const firstFieldError = Object.values(data)
    .flat()
    .find((item) => typeof item === "string");

  return (
    firstFieldError ||
    "Unable to submit payment proof. Please review your details and try again."
  );
};

const PaymentModal = ({ show, onHide, product }) => {
  const [prepareStep, setPrepareStep] = useState(0);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [proofError, setProofError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    note: "",
  });

  const quantity = Math.max(1, Number(product?.quantity || 1));
  const unitPrice = parseMoney(product?.unitPrice);
  const shippingFee = parseMoney(product?.shippingFee);
  const subtotal = unitPrice * quantity;
  const totalAmount = subtotal + shippingFee;
  const ready = show && prepareStep >= preparingMessages.length;
  const canRetry = ready && Boolean(submitError) && !submitting;

  const summary = useMemo(
    () => ({
      ...product,
      quantity,
      subtotal,
      totalAmount,
      shippingFee,
    }),
    [product, quantity, subtotal, totalAmount, shippingFee]
  );

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    setPrepareStep(0);

    const timer = setInterval(() => {
      setPrepareStep((step) => {
        if (step >= preparingMessages.length) {
          clearInterval(timer);
          return step;
        }

        return step + 1;
      });
    }, 450);

    return () => clearInterval(timer);
  }, [show]);

  useEffect(() => {
    return () => {
      if (proofPreview) {
        URL.revokeObjectURL(proofPreview);
      }
    };
  }, [proofPreview]);

  const resetProof = () => {
    setProofFile(null);

    if (proofPreview) {
      URL.revokeObjectURL(proofPreview);
    }

    setProofPreview("");
    setProofError("");
  };

  const close = () => {
    if (submitting) {
      return;
    }

    resetProof();
    setSubmitError("");
    onHide?.();
  };

  const handleProofChange = (event) => {
    const file = event.target.files?.[0];

    setProofError("");
    setSubmitError("");
    setProofFile(null);

    if (proofPreview) {
      URL.revokeObjectURL(proofPreview);
    }

    setProofPreview("");

    if (!file) {
      return;
    }

    if (!file.type?.startsWith("image/")) {
      setProofError("Please upload an image proof of payment.");
      return;
    }

    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
  };

  const handleCustomerChange = (field, value) => {
    setCustomer((prev) => ({
      ...prev,
      [field]: value,
    }));

    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitError("");
    setProofError("");

    if (!customer.name.trim() || !customer.phone.trim()) {
      setSubmitError(
        "Enter your name and phone number so we can confirm your order."
      );
      return;
    }

    if (!proofFile) {
      setProofError("Upload proof of payment before submitting.");
      setSubmitError("Upload proof of payment before submitting.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...summary,
        customerName: customer.name.trim(),
        customerPhone: customer.phone.trim(),
        customerEmail: customer.email.trim(),
        customerNote: customer.note.trim(),
        proofFile,
      };

      const response = await createManualPaymentOrder(payload);
      const savedOrder = response.data;
      const message = buildManualPaymentWhatsAppMessage({
        order: savedOrder,
        product: summary,
      });

      window.open(buildWhatsAppUrl(message), "_blank", "noopener,noreferrer");

      resetProof();
      setSubmitError("");
      onHide?.();
    } catch (error) {
      setSubmitError(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={close}
      backdrop="static"
      keyboard={!submitting}
      size="lg"
      centered
      scrollable
      dialogClassName="payment-modal-dialog"
      contentClassName="border-0 rounded-4 overflow-hidden"
    >
      <Modal.Header
        closeButton={!submitting}
        className="bg-white"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <Modal.Title>Manual Bank Transfer</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body
          style={{
            maxHeight: "calc(100vh - 220px)",
            overflowY: "auto",
          }}
        >
          {!ready ? (
            <div className="text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <h6>
                {
                  preparingMessages[
                    Math.min(prepareStep, preparingMessages.length - 1)
                  ]
                }
              </h6>
            </div>
          ) : (
            <Row className="g-3">
              <Col xs={12} lg={6}>
                <Card className="shadow-sm border-0 rounded-4 h-100">
                  <Card.Body>
                    <h6 className="text-uppercase text-muted">
                      Order Summary
                    </h6>

                    <div className="d-flex gap-3 mb-3">
                      {summary?.productImageUrl && (
                        <Image
                          src={summary.productImageUrl}
                          alt={summary.productName}
                          rounded
                          style={{
                            width: 82,
                            height: 82,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <div className="min-w-0">
                        <div className="fw-semibold">
                          {summary?.productName}
                        </div>
                        <small className="text-muted">
                          <Badge bg="light" text="dark">
                            {summary?.productSource}
                          </Badge>
                        </small>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <span>Unit Price</span>
                      <strong>{formatNaira(unitPrice)}</strong>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <span>Quantity</span>
                      <strong>{quantity}</strong>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <span>Subtotal</span>
                      <strong>{formatNaira(subtotal)}</strong>
                    </div>

                    {summary.shippingMethod && (
                      <>
                        <hr />
                        <div className="small text-muted">Shipping</div>
                        <div className="fw-semibold">
                          {summary.shippingMethod}
                        </div>

                        {summary.shippingEta && (
                          <div className="small text-muted">
                            ETA: {summary.shippingEta}
                          </div>
                        )}

                        <div className="d-flex justify-content-between gap-3 mt-1">
                          <span>Shipping Fee</span>
                          <strong>{formatNaira(shippingFee)}</strong>
                        </div>
                      </>
                    )}

                    <hr />

                    <div className="d-flex justify-content-between gap-3 fs-5">
                      <span>Total Payable</span>
                      <strong>{formatNaira(totalAmount)}</strong>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} lg={6}>
                <Card className="shadow-sm border-0 rounded-4 h-100">
                  <Card.Body>
                    <h6 className="text-uppercase text-muted">
                      Pay Into This Account
                    </h6>

                    <div className="bg-light rounded-3 p-3 mb-3">
                      <div>
                        <small>Account Name</small>
                        <div className="fw-bold">
                          {PAYMENT_ACCOUNT.accountName}
                        </div>
                      </div>

                      <div className="mt-2">
                        <small>Bank</small>
                        <div className="fw-bold">
                          {PAYMENT_ACCOUNT.bankName}
                        </div>
                      </div>

                      <div className="mt-2">
                        <small>Account Number</small>
                        <div className="fw-bold fs-5">
                          {PAYMENT_ACCOUNT.accountNumber}
                        </div>
                      </div>
                    </div>

                    <Alert variant="info" className="small">
                      After submitting your proof of payment, you will be
                      redirected to our customer care agent on WhatsApp to
                      confirm your purchase.
                    </Alert>

                    <Row className="g-2">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            value={customer.name}
                            onChange={(e) =>
                              handleCustomerChange("name", e.target.value)
                            }
                            disabled={submitting}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            value={customer.phone}
                            onChange={(e) =>
                              handleCustomerChange("phone", e.target.value)
                            }
                            disabled={submitting}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Email (optional)</Form.Label>
                          <Form.Control
                            type="email"
                            value={customer.email}
                            onChange={(e) =>
                              handleCustomerChange("email", e.target.value)
                            }
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Note (optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={customer.note}
                            onChange={(e) =>
                              handleCustomerChange("note", e.target.value)
                            }
                            disabled={submitting}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12}>
                <Card className="shadow-sm border-0 rounded-4">
                  <Card.Body>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Upload proof of payment
                      </Form.Label>

                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleProofChange}
                        disabled={submitting}
                      />

                      {proofError && (
                        <div className="text-danger small mt-2">
                          {proofError}
                        </div>
                      )}
                    </Form.Group>

                    {proofPreview && (
                      <div className="mt-3">
                        <Image
                          src={proofPreview}
                          alt="Proof preview"
                          thumbnail
                          style={{
                            width: "100%",
                            maxWidth: 360,
                            maxHeight: 220,
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer
          className="bg-white"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 20,
            boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div className="w-100">
            {submitError && (
              <Alert
                variant="danger"
                className="py-2 px-3 mb-3"
                style={{
                  wordBreak: "break-word",
                }}
              >
                <div className="fw-semibold mb-1">
                  Payment submission failed
                </div>
                <div className="small">{submitError}</div>
              </Alert>
            )}

            <div className="d-grid d-sm-flex justify-content-sm-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={close}
                disabled={submitting}
                className="w-100 w-sm-auto"
              >
                Close
              </Button>

              {canRetry && (
                <Button
                  type="submit"
                  variant="warning"
                  disabled={submitting}
                  className="w-100 w-sm-auto fw-semibold"
                >
                  Retry Submission
                </Button>
              )}

              <Button
                type="submit"
                variant="success"
                disabled={!ready || submitting}
                className="w-100 w-sm-auto fw-semibold"
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Proof & Continue to WhatsApp"
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentModal;